import { getTenant } from '../../../theme/tokens'
import { nowMs } from '../../../shared/lib/relativeDates'
import {
  makeCompanyName,
  timeZoneAbbr,
  tenantTimeZone,
} from '../../../shared/config/localeProfile'
import { BRAND } from '../../../shared/config/brand'

// ── Domain types ─────────────────────────────────────────────────────────

export type PayoutStatus =
  | 'pending'
  | 'submitted'
  | 'partially_submitted'
  | 'failed'
  | 'cancelled'

export type TransferStatus = 'pending' | 'submitted' | 'failed' | 'cancelled'

/**
 * There is ONE payout status vocabulary — PayoutStatus — and it is used
 * verbatim everywhere a payout status is shown (list, detail, filters, the
 * status tooltip, exports). No page derives its own alternate labels.
 *
 * We deliberately don't model an "in transit" / "funds arriving" state.
 * Once a payout is submitted we can't reliably observe when the destination
 * account is credited (banks take 1–5 business days and don't notify us), so
 * claiming "in transit" would be a prediction we can't back up. 'submitted'
 * means we've handed it off; the merchant checks their bank for arrival.
 */

// ── Dynamic payout calculation model ──────────────────────────────────────
// A payout is Gross amount + a SIGNED list of adjustments. The list is
// open-ended: any number of current or future adjustment kinds can appear.
// Net payout is ALWAYS grossAmount + sum(adjustments). We never model a
// fixed set of financial columns — new adjustment types simply show up as
// new line items in the Payout calculation.
// Stable, merchant-facing line-item kinds. This set maps 1:1 to the labels
// shown in the Payout calculation and is bank/market agnostic — subscription
// charges, terminal rental, service or statement charges all surface as the
// single generic "Account charges" kind; the underlying subtype is never
// exposed here.
export type PayoutAdjustmentKind =
  // Payment activity
  | 'refunds'
  | 'chargebacks'
  | 'chargeback_reversals'
  // Fees and taxes
  | 'fees'
  | 'vat_on_fee'
  | 'wht_adjustment'
  // Reserve adjustments
  | 'reserve_withheld'
  | 'reserve_released'
  // Minimum balance adjustments
  | 'minimum_balance_retained'
  | 'minimum_balance_released'
  // Account charges
  | 'account_charges'
  | 'vat_on_account_charges'
  | 'wht_on_account_charges'
  // Other adjustments
  | 'manual_adjustment'
  | 'negative_balance_recovery'
  | 'other_adjustment'

export type PayoutCalcGroup =
  | 'payment_activity'
  | 'fees_taxes'
  | 'reserve_adjustments'
  | 'minimum_balance_adjustments'
  | 'account_charges'
  | 'other_adjustments'

export interface PayoutLineItem {
  kind: PayoutAdjustmentKind
  label: string
  group: PayoutCalcGroup
  /** Signed. Negative reduces the payout, positive increases it. */
  amount: number
}

const ADJUSTMENT_META: Record<
  PayoutAdjustmentKind,
  { label: string; group: PayoutCalcGroup }
> = {
  // Payment activity
  refunds: { label: 'Refunds', group: 'payment_activity' },
  chargebacks: { label: 'Chargebacks', group: 'payment_activity' },
  chargeback_reversals: {
    label: 'Chargeback reversals',
    group: 'payment_activity',
  },
  // Fees and taxes
  fees: { label: 'Transaction fees', group: 'fees_taxes' },
  vat_on_fee: { label: 'VAT on transaction fees', group: 'fees_taxes' },
  wht_adjustment: { label: 'WHT adjustment', group: 'fees_taxes' },
  // Reserve adjustments
  reserve_withheld: {
    label: 'Reserve withheld',
    group: 'reserve_adjustments',
  },
  reserve_released: {
    label: 'Reserve released',
    group: 'reserve_adjustments',
  },
  // Minimum balance adjustments
  minimum_balance_retained: {
    label: 'Minimum balance retained',
    group: 'minimum_balance_adjustments',
  },
  minimum_balance_released: {
    label: 'Minimum balance released',
    group: 'minimum_balance_adjustments',
  },
  // Account charges — generic merchant-facing billing term. The individual
  // account-charge subtype (subscription, terminal rental, service, statement)
  // is intentionally NOT exposed in the payout calculation.
  account_charges: { label: 'Account charges', group: 'account_charges' },
  vat_on_account_charges: {
    label: 'VAT on account charges',
    group: 'account_charges',
  },
  wht_on_account_charges: {
    label: 'WHT adjustment on account charges',
    group: 'account_charges',
  },
  // Other adjustments
  manual_adjustment: { label: 'Manual adjustment', group: 'other_adjustments' },
  negative_balance_recovery: {
    label: 'Negative balance recovery',
    group: 'other_adjustments',
  },
  other_adjustment: { label: 'Other adjustment', group: 'other_adjustments' },
}

const GROUP_LABEL: Record<PayoutCalcGroup, string> = {
  payment_activity: 'Payment activity',
  fees_taxes: 'Fees and taxes',
  reserve_adjustments: 'Reserve adjustments',
  minimum_balance_adjustments: 'Minimum balance adjustments',
  account_charges: 'Account charges',
  other_adjustments: 'Other adjustments',
}

export interface PayoutTransfer {
  transferId: string
  status: TransferStatus
  /** Transfers only surface the net amount they carry. */
  netAmount: number
  bankReference: string
  description: string
}

export interface Payout {
  payoutId: string
  merchantEntity: string
  status: PayoutStatus
  /**
   * THE payout date — the single date this payout was submitted to the bank,
   * or for a 'pending' payout, the date it is scheduled to be submitted.
   *
   * This is the only date a payout has. We deliberately do NOT model a
   * separate "created at", "submitted at", "initiated at" or value/effective
   * date: we have no observable creation timestamp distinct from submission,
   * and we can't observe when the destination account is credited. Inventing
   * those dates produced several labels for one value.
   *
   * Formatted for display in the tenant timezone.
   */
  payoutDate: string
  /** Raw Date backing `payoutDate`, for sorting and filtering. */
  payoutDateRaw: Date
  /**
   * When the payout record was created — the payout cycle cutting the batch.
   * A real, distinct event: always strictly before `payoutDate`, and in the
   * past even for a pending payout whose payout date is still in the future.
   * Every payout has one, regardless of status.
   */
  createdAt: string
  /** Raw Date backing `createdAt`. */
  createdAtRaw: Date
  currency: string
  grossAmount: number
  /** Signed, only-applicable line items. Net = gross + sum(adjustments). */
  adjustments: PayoutLineItem[]
  /** Signed sum of all adjustments. */
  netAdjustments: number
  /** grossAmount + netAdjustments. */
  netAmount: number
  destinationAccount: string
  transfers: PayoutTransfer[]
}

interface RawTransferEntry {
  merchantEntity: string
  payoutId: string
  payoutStatus: 'Submitted' | 'Pending' | 'Failed'
  /** Tenant-local wall time, e.g. "May 6, 2026 15:00:00". */
  payoutDate: string
  /**
   * When the payout record was created (the payout cycle cutting the batch).
   * Tenant-local wall time. A REAL field carried per record — never derived
   * from the payout date — and always strictly before it.
   */
  createdAt: string
  currency: string
  grossAmount: number
  destinationAccount: string
  description: string
  externalPayoutReference: string
}

// ── Source data ──────────────────────────────────────────────────────────
// All payouts settle into the merchant's CIMB account. Suffix
// matches the bankSuffix mock pattern used elsewhere in the balance feature.

const DESTINATION = `${BRAND.shortCode} •••• 4521`

// Bank references in the raw data carry a legacy "BML-" prefix; surface them
// with the active brand's short code so labels follow the tenant.
function displayBankRef(ref: string): string {
  return ref.replace(/^BML/, BRAND.shortCode)
}

function rawTransfer(
  payoutId: string,
  status: 'Submitted' | 'Pending' | 'Failed',
  payoutDate: string,
  createdAt: string,
  gross: number,
  description: string,
  externalRef: string,
): RawTransferEntry {
  return {
    merchantEntity: makeCompanyName(`${BRAND.name} Merchant`),
    payoutId,
    payoutStatus: status,
    payoutDate,
    createdAt,
    currency: getTenant().currency.code,
    grossAmount: gross,
    destinationAccount: DESTINATION,
    description,
    externalPayoutReference: externalRef,
  }
}

// Each record carries BOTH dates explicitly: when the payout record was
// created (the cycle cutting the batch, late evening) and the payout date
// itself. Transfers belonging to the same payout share the same created
// timestamp because they were cut in the same batch.
const rawTransfers: RawTransferEntry[] = [
  rawTransfer(
    'up_pending_may6_2026_001',
    'Pending',
    'May 6, 2026 15:00:00',
    'May 5, 2026 22:14:00',
    46630.72,
    'Upcoming payout for 6 May 2026',
    'BML-20260506-TXN-00185',
  ),
  rawTransfer(
    '69a608748649da0d81711cc',
    'Submitted',
    'May 7, 2026 15:00:00',
    'May 6, 2026 22:08:00',
    2000000.0,
    '1 of 3 for 7 May 2026',
    'BML-20260507-TXN-00184',
  ),
  rawTransfer(
    '69a608748649da0d81712dd',
    'Submitted',
    'May 7, 2026 15:00:00',
    'May 6, 2026 22:08:00',
    2000000.0,
    '2 of 3 for 7 May 2026',
    'BML-20260507-TXN-00184',
  ),
  rawTransfer(
    '69a608748649da0d81713ee',
    'Submitted',
    'May 7, 2026 15:00:00',
    'May 6, 2026 22:08:00',
    1847300.0,
    '3 of 3 for 7 May 2026',
    'BML-20260507-TXN-00184',
  ),
  rawTransfer(
    '7a1b2c3d4e5f60718a9b0c1',
    'Submitted',
    'May 6, 2026 15:00:00',
    'May 5, 2026 22:11:00',
    1245080.0,
    '1 of 1 for 6 May 2026',
    'BML-20260506-TXN-00183',
  ),
  rawTransfer(
    '8b2c3d4e5f6a71829c0d1e2',
    'Submitted',
    'May 5, 2026 15:00:00',
    'May 4, 2026 22:03:00',
    2000000.0,
    '1 of 3 for 5 May 2026',
    'BML-20260505-TXN-00179',
  ),
  rawTransfer(
    '8b2c3d4e5f6a71829c0d2f3',
    'Submitted',
    'May 5, 2026 15:00:00',
    'May 4, 2026 22:03:00',
    2000000.0,
    '2 of 3 for 5 May 2026',
    'BML-20260505-TXN-00179',
  ),
  rawTransfer(
    '8b2c3d4e5f6a71829c0d3g4',
    'Submitted',
    'May 5, 2026 15:00:00',
    'May 4, 2026 22:03:00',
    210500.0,
    '3 of 3 for 5 May 2026',
    'BML-20260505-TXN-00179',
  ),
  rawTransfer(
    '9c3d4e5f6g7h81930k1l2m3',
    'Submitted',
    'May 4, 2026 15:00:00',
    'May 3, 2026 22:17:00',
    875000.0,
    '1 of 1 for 4 May 2026',
    'BML-20260504-TXN-00175',
  ),
  rawTransfer(
    '0d4e5f6g7h8i92041l2m3n4',
    'Submitted',
    'May 3, 2026 15:00:00',
    'May 2, 2026 22:06:00',
    567120.0,
    '1 of 1 for 3 May 2026',
    'BML-20260503-TXN-00172',
  ),
  rawTransfer(
    'ae5f6g7h8i9j03152m3n4o5',
    'Submitted',
    'May 2, 2026 15:00:00',
    'May 1, 2026 22:12:00',
    1530000.0,
    '1 of 2 for 2 May 2026',
    'BML-20260502-TXN-00168',
  ),
  rawTransfer(
    'bf6g7h8i9j0k14263n4o5p6',
    'Submitted',
    'May 2, 2026 15:00:00',
    'May 1, 2026 22:12:00',
    980500.0,
    '2 of 2 for 2 May 2026',
    'BML-20260502-TXN-00168',
  ),
  rawTransfer(
    'cg7h8i9j0k1l25374o5p6q7',
    'Submitted',
    'May 1, 2026 15:00:00',
    'Apr 30, 2026 22:09:00',
    2000000.0,
    '1 of 3 for 1 May 2026',
    'BML-20260501-TXN-00165',
  ),
  rawTransfer(
    'dh8i9j0k1l2m36485p6q7r8',
    'Submitted',
    'May 1, 2026 15:00:00',
    'Apr 30, 2026 22:09:00',
    2000000.0,
    '2 of 3 for 1 May 2026',
    'BML-20260501-TXN-00165',
  ),
  rawTransfer(
    'ei9j0k1l2m3n47596q7r8s9',
    'Submitted',
    'May 1, 2026 15:00:00',
    'Apr 30, 2026 22:09:00',
    742650.0,
    '3 of 3 for 1 May 2026',
    'BML-20260501-TXN-00165',
  ),
  rawTransfer(
    'fj0k1l2m3n4o586a7r8s9t0',
    'Submitted',
    'Apr 29, 2026 15:00:00',
    'Apr 28, 2026 22:15:00',
    1125000.0,
    '1 of 1 for 29 Apr 2026',
    'BML-20260429-TXN-00160',
  ),
  rawTransfer(
    'gk1l2m3n4o5p697b8s9t0u1',
    'Submitted',
    'Apr 27, 2026 15:00:00',
    'Apr 26, 2026 22:04:00',
    1890000.0,
    '1 of 2 for 27 Apr 2026',
    'BML-20260427-TXN-00155',
  ),
  rawTransfer(
    'hl2m3n4o5p6q708c9t0u1v2',
    'Submitted',
    'Apr 27, 2026 15:00:00',
    'Apr 26, 2026 22:04:00',
    435200.0,
    '2 of 2 for 27 Apr 2026',
    'BML-20260427-TXN-00155',
  ),
  rawTransfer(
    'im3n4o5p6q7r819d0u1v2w3',
    'Submitted',
    'Apr 25, 2026 15:00:00',
    'Apr 24, 2026 22:19:00',
    650000.0,
    '1 of 1 for 25 Apr 2026',
    'BML-20260425-TXN-00150',
  ),
  rawTransfer(
    'jn4o5p6q7r8s920e1v2w3x4',
    'Submitted',
    'Apr 23, 2026 15:00:00',
    'Apr 22, 2026 22:07:00',
    2000000.0,
    '1 of 2 for 23 Apr 2026',
    'BML-20260423-TXN-00145',
  ),
  rawTransfer(
    'ko5p6q7r8s9t031f2w3x4y5',
    'Submitted',
    'Apr 23, 2026 15:00:00',
    'Apr 22, 2026 22:07:00',
    315700.0,
    '2 of 2 for 23 Apr 2026',
    'BML-20260423-TXN-00145',
  ),
  rawTransfer(
    'lp6q7r8s9t0u142g3x4y5z6',
    'Submitted',
    'Apr 21, 2026 15:00:00',
    'Apr 20, 2026 22:13:00',
    1450000.0,
    '1 of 1 for 21 Apr 2026',
    'BML-20260421-TXN-00140',
  ),
  rawTransfer(
    'mq7r8s9t0u1v253h4y5z6a7',
    'Submitted',
    'Apr 19, 2026 15:00:00',
    'Apr 18, 2026 22:02:00',
    780000.0,
    '1 of 1 for 19 Apr 2026',
    'BML-20260419-TXN-00135',
  ),
  rawTransfer(
    'nr8s9t0u1v2w364i5z6a7b8',
    'Submitted',
    'Apr 16, 2026 15:00:00',
    'Apr 15, 2026 22:16:00',
    1680000.0,
    '1 of 2 for 16 Apr 2026',
    'BML-20260416-TXN-00130',
  ),
  rawTransfer(
    'os9t0u1v2w3x475j6a7b8c9',
    'Submitted',
    'Apr 16, 2026 15:00:00',
    'Apr 15, 2026 22:16:00',
    520370.0,
    '2 of 2 for 16 Apr 2026',
    'BML-20260416-TXN-00130',
  ),
]

// ── Transform ────────────────────────────────────────────────────────────

/**
 * Parse a mock payout date literal, written as tenant-local wall time
 * (e.g. "May 6, 2026 15:00:00"). Mock data only — the +8 offset matches the
 * tenant timezone (MYT). Display always goes through `formatPayoutDate`, so
 * the label shown to merchants can't drift from the offset used here.
 */
const TENANT_UTC_OFFSET_MS = 8 * 60 * 60 * 1000
function parsePayoutDate(s: string): Date {
  const ts = Date.parse(s + ' UTC')
  return new Date(ts - TENANT_UTC_OFFSET_MS)
}

/**
 * The one place a payout date becomes display text. Every surface (list,
 * detail, exports) formats through here, so the timezone label and the
 * offset can't disagree.
 */
export function formatPayoutDate(d: Date): string {
  const s = d.toLocaleString('en-GB', {
    timeZone: tenantTimeZone(),
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${s.replace(',', '')} ${timeZoneAbbr()}`
}

/**
 * Deterministically build the signed adjustment line items for a payout.
 * Fees / VAT / WHT always apply; the remaining kinds vary per payout so the
 * demo shows a range of calculation shapes. The set is open-ended — adding a
 * new kind here makes it flow through the list, the Net adjustments column,
 * and the Payout calculation with zero column changes downstream.
 */
function buildAdjustments(gross: number, idNum: number): PayoutLineItem[] {
  const rng = (i: number) => {
    const x = Math.sin(idNum * 7 + i * 13) * 10000
    return x - Math.floor(x)
  }
  const raw: { kind: PayoutAdjustmentKind; amount: number }[] = []

  // ── Showcase payout (po_10002) ──
  // A single reference payout that carries ONE line item from every category
  // so engineers can see exactly where each adjustment kind belongs in the
  // Payout calculation. Signs follow the documented convention. Fees/VAT/WHT
  // are derived from gross; the rest use realistic fixed sample amounts.
  if (idNum === 10002) {
    const fees = -round2(gross * 0.02826)
    const vat = -round2(Math.abs(fees) * 0.07)
    const wht = round2(gross * 0.000847)
    const showcase: { kind: PayoutAdjustmentKind; amount: number }[] = [
      // Payment activity
      { kind: 'refunds', amount: -round2(200 + rng(2) * 4000) },
      { kind: 'chargebacks', amount: -round2(150 + rng(4) * 600) },
      { kind: 'chargeback_reversals', amount: round2(100 + rng(6) * 500) },
      // Fees and taxes
      { kind: 'fees', amount: fees },
      { kind: 'vat_on_fee', amount: vat },
      { kind: 'wht_adjustment', amount: wht },
      // Reserve adjustments
      { kind: 'reserve_withheld', amount: -round2(400 + rng(12) * 2000) },
      { kind: 'reserve_released', amount: round2(400 + rng(13) * 2000) },
      // Minimum balance adjustments
      {
        kind: 'minimum_balance_retained',
        amount: -round2(500 + rng(10) * 2500),
      },
      {
        kind: 'minimum_balance_released',
        amount: round2(300 + rng(11) * 1500),
      },
      // Account charges
      { kind: 'account_charges', amount: -round2(200 + rng(17) * 800) },
      { kind: 'vat_on_account_charges', amount: -round2(20 + rng(18) * 40) },
      { kind: 'wht_on_account_charges', amount: round2(10 + rng(19) * 20) },
      // Other adjustments
      { kind: 'manual_adjustment', amount: round2(50 + rng(16) * 400) },
      {
        kind: 'negative_balance_recovery',
        amount: -round2(100 + rng(14) * 900),
      },
      { kind: 'other_adjustment', amount: -round2(50 + rng(21) * 300) },
    ]
    return showcase
      .filter((r) => r.amount !== 0)
      .map((r) => ({
        kind: r.kind,
        amount: r.amount,
        label: ADJUSTMENT_META[r.kind].label,
        group: ADJUSTMENT_META[r.kind].group,
      }))
  }

  // ── Payment activity ──
  // Refunds / chargebacks reduce the gross; chargeback reversals add back.
  if (rng(1) > 0.55)
    raw.push({ kind: 'refunds', amount: -round2(200 + rng(2) * 4000) })
  if (rng(3) > 0.85)
    raw.push({ kind: 'chargebacks', amount: -round2(150 + rng(4) * 600) })
  if (rng(5) > 0.9)
    raw.push({
      kind: 'chargeback_reversals',
      amount: round2(100 + rng(6) * 500),
    })

  // ── Fees and taxes ── (transaction fees always apply)
  const fees = -round2(gross * 0.02826)
  const vat = -round2(Math.abs(fees) * 0.07)
  const wht = round2(gross * 0.000847)
  raw.push({ kind: 'fees', amount: fees })
  raw.push({ kind: 'vat_on_fee', amount: vat })
  raw.push({ kind: 'wht_adjustment', amount: wht })

  const mod = idNum % 10

  // ── Reserve adjustments ──
  if (mod === 7)
    raw.push({
      kind: 'reserve_withheld',
      amount: -round2(400 + rng(12) * 2000),
    })
  if (mod === 8)
    raw.push({
      kind: 'reserve_released',
      amount: round2(400 + rng(13) * 2000),
    })

  // ── Minimum balance adjustments ──
  if (mod === 0 || mod === 3 || mod === 6)
    raw.push({
      kind: 'minimum_balance_retained',
      amount: -round2(500 + rng(10) * 2500),
    })
  if (mod === 2)
    raw.push({
      kind: 'minimum_balance_released',
      amount: round2(300 + rng(11) * 1500),
    })

  // ── Account charges ── (merchant-level billing; subtype never exposed)
  if (mod === 1 || mod === 5) {
    const charge = -round2(200 + rng(17) * 800)
    const chargeVat = -round2(Math.abs(charge) * 0.07)
    const chargeWht = round2(Math.abs(charge) * 0.03)
    raw.push({ kind: 'account_charges', amount: charge })
    raw.push({ kind: 'vat_on_account_charges', amount: chargeVat })
    if (rng(18) > 0.5)
      raw.push({ kind: 'wht_on_account_charges', amount: chargeWht })
  }

  // ── Other adjustments ──
  if (mod === 9)
    raw.push({
      kind: 'manual_adjustment',
      amount: (rng(15) > 0.5 ? 1 : -1) * round2(50 + rng(16) * 400),
    })
  if (mod === 4)
    raw.push({
      kind: 'negative_balance_recovery',
      amount: -round2(100 + rng(14) * 900),
    })
  if (mod === 6 && rng(19) > 0.7)
    raw.push({
      kind: 'other_adjustment',
      amount: (rng(20) > 0.5 ? 1 : -1) * round2(50 + rng(21) * 300),
    })

  return raw
    .filter((r) => r.amount !== 0)
    .map((r) => ({
      kind: r.kind,
      amount: r.amount,
      label: ADJUSTMENT_META[r.kind].label,
      group: ADJUSTMENT_META[r.kind].group,
    }))
}

function transformToPayouts(transfers: RawTransferEntry[]): Payout[] {
  const grouped = new Map<string, RawTransferEntry[]>()
  for (const t of transfers) {
    const key = t.externalPayoutReference
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key)!.push(t)
  }
  const payouts: Payout[] = []
  let payoutCounter = 10001

  // Special-cases — sprinkle in status variety so the detail page can
  // demonstrate all the timeline / pill states.
  const specialStatuses = new Map<string, PayoutStatus>([
    ['BML-20260505-TXN-00179', 'partially_submitted'],
  ])

  Array.from(grouped.entries()).forEach(([bankRef, group]) => {
    const first = group[0]
    const idNum = payoutCounter
    const payoutId = `po_${payoutCounter++}`
    const grossAmount = round2(group.reduce((s, t) => s + t.grossAmount, 0))
    const adjustments = buildAdjustments(grossAmount, idNum)
    const netAdjustments = round2(adjustments.reduce((s, a) => s + a.amount, 0))
    const netAmount = round2(grossAmount + netAdjustments)
    const rawStatus = first.payoutStatus.toLowerCase() as PayoutStatus
    const status: PayoutStatus = specialStatuses.get(bankRef) || rawStatus

    // Distribute the net payout across transfers proportionally to their
    // gross share so the transfer amounts always sum back to Net payout.
    const totalGross = group.reduce((s, t) => s + t.grossAmount, 0) || 1
    const payoutDateRaw = parsePayoutDate(first.payoutDate)
    const createdAtRaw = parsePayoutDate(first.createdAt)
    let allocated = 0
    const transfers: PayoutTransfer[] = group.map((t, idx) => {
      let transferStatus: TransferStatus = 'submitted'
      if (status === 'partially_submitted' && idx === group.length - 1)
        transferStatus = 'failed'
      if (status === 'pending') transferStatus = 'pending'
      let transferNet: number
      if (idx === group.length - 1) {
        transferNet = round2(netAmount - allocated)
      } else {
        transferNet = round2(netAmount * (t.grossAmount / totalGross))
        allocated = round2(allocated + transferNet)
      }
      return {
        transferId: `pt_${String(idx + 1).padStart(3, '0')}`,
        status: transferStatus,
        netAmount: transferNet,
        bankReference: `${displayBankRef(t.externalPayoutReference)}-${String(idx + 1).padStart(2, '0')}`,
        description: t.description,
      }
    })

    payouts.push({
      payoutId,
      merchantEntity: first.merchantEntity,
      status,
      payoutDate: formatPayoutDate(payoutDateRaw),
      payoutDateRaw,
      createdAt: formatPayoutDate(createdAtRaw),
      createdAtRaw,
      currency: first.currency,
      grossAmount,
      adjustments,
      netAdjustments,
      netAmount,
      destinationAccount: first.destinationAccount,
      transfers,
    })
  })

  payouts.sort(
    (a, b) => b.payoutDateRaw.getTime() - a.payoutDateRaw.getTime(),
  )
  return payouts
}

const round2 = (n: number) => Math.round(n * 100) / 100

// ── Public API ───────────────────────────────────────────────────────────

let cachedPayouts: Payout[] | null = null

export function getAllPayouts(): Payout[] {
  if (!cachedPayouts) cachedPayouts = transformToPayouts(rawTransfers)
  return cachedPayouts
}

export function getPayoutById(id: string): Payout | undefined {
  return getAllPayouts().find((p) => p.payoutId === id)
}

// ── Bank account details ─────────────────────────────────────────────────

export interface PayoutAccountDetails {
  bank: string
  bankShort: string
  last4: string
  type: string
  accountHolder: string
  origin: { country: string; flag: string }
  fingerprint: string
  routingNumber: string
  bankAccountId: string
}

export function getAccountDetails(payout: Payout): PayoutAccountDetails {
  return {
    bank: BRAND.name,
    bankShort: BRAND.shortCode,
    last4: '4521',
    type: 'Bank account',
    accountHolder: payout.merchantEntity.toUpperCase(),
    origin: { country: 'Malaysia', flag: '🇲🇾' },
    fingerprint: 'RbnICHwZlZtd37Nt',
    routingNumber: BRAND.bank.swift,
    bankAccountId: `ba_1${payout.payoutId
      .replace(/[^a-z0-9]/gi, '')
      .padEnd(20, '0')
      .slice(0, 20)}`,
  }
}

// ── Payout calculation ────────────────────────────────────────────────────
// The Payout detail page renders a dynamic, vertically-stacked calculation:
// the Gross payment amount followed by every applicable signed adjustment,
// optionally grouped. Only-applicable items are stored on the payout, so the
// calculation naturally grows/shrinks with the data — no fixed columns.

export interface PayoutCalcRow {
  label: string
  /** Signed amount. */
  amount: number
}

export interface PayoutCalcSection {
  key: PayoutCalcGroup
  label: string
  rows: PayoutCalcRow[]
}

/**
 * Build the grouped ADJUSTMENT sections for a payout.
 *
 * Gross payment amount is deliberately NOT included: it is the calculation's
 * starting point, not an adjustment. Keeping it out means these sections are
 * exactly the set that sums to Net adjustments, so the on-screen grouping and
 * the total can't drift apart. Empty groups are omitted so zero /
 * non-applicable items never render.
 */
export function getPayoutCalcSections(payout: Payout): PayoutCalcSection[] {
  const byGroup: Record<PayoutCalcGroup, PayoutCalcRow[]> = {
    payment_activity: [],
    fees_taxes: [],
    reserve_adjustments: [],
    minimum_balance_adjustments: [],
    account_charges: [],
    other_adjustments: [],
  }
  for (const a of payout.adjustments) {
    // Skip any zero-amount item so empty rows never render or reserve space.
    if (a.amount === 0) continue
    byGroup[a.group].push({ label: a.label, amount: a.amount })
  }
  const order: PayoutCalcGroup[] = [
    'payment_activity',
    'fees_taxes',
    'reserve_adjustments',
    'minimum_balance_adjustments',
    'account_charges',
    'other_adjustments',
  ]
  return order
    .map((key) => ({ key, label: GROUP_LABEL[key], rows: byGroup[key] }))
    .filter((s) => s.rows.length > 0)
}

// ── Trace ID ─────────────────────────────────────────────────────────────

/**
 * Bank-side trace ID merchants reconcile against their statements. Format
 * mimics Malaysian interbank reference style.
 */
export function getPayoutTraceId(payout: Payout): string {
  const eff = payout.payoutDateRaw
  const yyyymmdd =
    eff.getFullYear().toString() +
    String(eff.getMonth() + 1).padStart(2, '0') +
    String(eff.getDate()).padStart(2, '0')
  const tail = payout.payoutId.slice(-6).toUpperCase().padStart(6, '0')
  return `MY2ON${yyyymmdd}${tail}`
}
